import { FC, PropsWithChildren } from 'react';

import { useUser } from '@auth-v2';
import { FloorConnection } from '@realtime';
import { useQueryClient } from '@tanstack/react-query';
import { ScopeProvider } from 'jotai-scope';

import { useSwingMeasuredParameters } from '@hooks';
import { useCorridors, useLayoutById, useReadOnlyCurrentActivity, useUserSettings, queryKeys } from '@queries';
import { LoadingScreen } from '@screens';
import {
    activityAtom,
    corridorsAtom,
    currentActivityAtom,
    layoutAtom,
    swingMeasuredParametersAtom,
    userLocaleAtom,
    userSettingsAtom,
    useSyncAtom,
} from '@store';

import { useSubscribeToFloorSettings } from '../hooks/useFloorSettings';
import { useSubscribeToActivity } from '../hooks/useSubscribeToActivity';
import { useSubscribeToLayout } from '../hooks/useSubscribeToLayout';
import { useSubscribeToUserSettings } from '../hooks/useSubscribeToUserSettings';

const UserScope: FC<PropsWithChildren> = ({ children }) => {
    const { data: user } = useUser();
    return (
        <ScopeProvider
            key={user?.id || 'none'}
            atoms={[currentActivityAtom, userLocaleAtom, userSettingsAtom, layoutAtom, corridorsAtom]}
        >
            {children}
        </ScopeProvider>
    );
};

const UserLoader: FC<PropsWithChildren> = ({ children }) => {
    const { data: user } = useUser();
    const queryClient = useQueryClient();
    const { data: userSettings } = useUserSettings();
    useSyncAtom(userSettingsAtom, userSettings);
    useSyncAtom(userLocaleAtom, userSettings?.locale);

    const { data: currentActivity } = useReadOnlyCurrentActivity();
    useSyncAtom(currentActivityAtom, currentActivity, true);
    useSyncAtom(activityAtom, currentActivity, true);
    useSubscribeToActivity(currentActivity?.id ?? -1);

    const { data: activeLayout } = useLayoutById(currentActivity?.layoutId ?? -1);
    useSyncAtom(layoutAtom, activeLayout);

    useSubscribeToLayout(currentActivity?.layoutId ?? -1);
    const { data: swingMeasuredParameters } = useSwingMeasuredParameters();
    useSyncAtom(swingMeasuredParametersAtom, swingMeasuredParameters);

    const { data: corridors } = useCorridors(userSettings?.userPreferredUnit ?? 'metric');
    useSyncAtom(corridorsAtom, corridors);

    useSubscribeToFloorSettings();
    useSubscribeToUserSettings();
    const isUserReady = userSettings && corridors && swingMeasuredParameters;

    return (
        <FloorConnection
            currentActivityId={currentActivity?.id}
            onActivityIdChange={(newActivityId) =>
                user &&
                (currentActivity?.id ?? null) !== newActivityId &&
                queryClient.invalidateQueries({ queryKey: queryKeys.activities.current(user.id) })
            }
        >
            {!isUserReady && <LoadingScreen typographyVariant="h2" message="Loading User Settings..." />}

            {isUserReady && !activeLayout && (
                <LoadingScreen typographyVariant="h2" message="Waiting for Activity Setup to finish..." />
            )}

            {isUserReady && activeLayout && children}
        </FloorConnection>
    );
};

export const UserGuard: FC<PropsWithChildren> = ({ children }) => {
    return (
        <UserScope>
            <UserLoader>{children}</UserLoader>
        </UserScope>
    );
};
